/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames/bind';

import { Icon } from '../../elements';
import styles from './ImageSlider.module.scss';

const cx = classNames.bind(styles);

const variants = {
	enter: (direction) => {
		return {
			x: direction > 0 ? 1000 : -1000,
			opacity: 0,
		};
	},
	center: {
		zIndex: 1,
		x: 0,
		opacity: 1,
	},
	exit: (direction) => {
		return {
			zIndex: 0,
			x: direction < 0 ? 1000 : -1000,
			opacity: 0,
		};
	},
};

const autoplayDelay = 4000;
const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
	return Math.abs(offset) * velocity;
};

const ImageSlider = ({ images, sliderHeight }) => {
	const [[imageIndex, direction], setPage] = React.useState([0, 0]);
	const [showControls, toggleControls] = React.useState(false);
	const timeoutRef = React.useRef(null);

	const resetTimeout = () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
	}

	const paginate = (newDirection) => {
		let currentIndex = imageIndex + newDirection;

		if (currentIndex >= images.length) currentIndex = 0;
		else if (currentIndex <= -1) currentIndex = images.length - 1;

		setPage([currentIndex, newDirection]);
	};

	React.useEffect(() => {
		resetTimeout();
		timeoutRef.current = setTimeout(() => {
			paginate(1);
		}, autoplayDelay);

		return () => resetTimeout();
	}, [imageIndex]);

	return (
		<React.Fragment>
			<div
				className={styles.sliderWrapper}
				style={{ height: sliderHeight }}
				onMouseEnter={() => toggleControls(true)}
				onMouseLeave={() => toggleControls(false)}>
				<AnimatePresence initial={false} custom={direction}>
					<motion.img
						key={imageIndex}
						src={images[imageIndex]}
						custom={direction}
						variants={variants}
						initial='enter'
						animate='center'
						exit='exit'
						transition={{
							x: { stiffness: 300, damping: 30 },
							opacity: { duration: 0.2 },
						}}
						drag='x'
						dragConstraints={{ left: 0, right: 0 }}
						dragElastic={1}
						onDragEnd={(e, { offset, velocity }) => {
							const swipe = swipePower(offset.x, velocity.x);

							if (swipe < -swipeConfidenceThreshold) {
								paginate(1);
							} else if (swipe > swipeConfidenceThreshold) {
								paginate(-1);
							}
						}}
					/>
				</AnimatePresence>
				<button className={cx('prevButton', { show: showControls })} onClick={() => paginate(-1)}>
					<Icon name='arrow-left' color='#ffffff' size='22' />
				</button>

				<button className={cx('nextButton', { show: showControls })} onClick={() => paginate(1)}>
					<Icon name='arrow-right' color='#ffffff' size='22' />
				</button>
			</div>

			<div className={styles.scrollBar}>
				<div
					className={styles.indicator}
					style={{
						width: `calc(100% / ${images.length})`,
						transform: `translateX(${100 * imageIndex}%)`,
					}}
				/>
			</div>
		</React.Fragment>
	);
};

ImageSlider.defaultProps = {
	sliderHeight: '300px',
};

ImageSlider.propTypes = {
	images: PropTypes.arrayOf(PropTypes.string).isRequired,
	sliderHeight: PropTypes.string,
};

export default ImageSlider;
